/**
 * A tag-style cell editor for entering multiple string values.
 * Stores values as a string[] array.
 * Users can type values and press comma/semicolon/enter to add tags.
 * Optionally shows autocomplete suggestions from the company text field options API.
 */
import { colorMiscBlue70, IconX } from "@rhumbix/rmbx_design_system_web"
import { forwardRef, useImperativeHandle, useState, useRef, KeyboardEvent, useEffect } from "react"
import { ICellEditorParams } from "ag-grid-community"

import { getDataFromSearchv4 } from "../../../api/get"
import {
    TagEditorContainer,
    TagsContainer,
    Tag,
    TagText,
    RemoveButton,
    TagInput,
    ErrorMessage,
} from "./tag-editor-styles"
import { useSuggestions, SuggestionsDropdown } from "./useSuggestions"

const WARNING_MESSAGE_DURATION = 3000

/**
 * Extended props for the StringTagCellEditor.
 * Includes optional suggestion source params passed via cellEditorParams.
 */
type tStringTagCellEditorParams = ICellEditorParams & {
    /** The model name for the companyTextFieldOptions search (e.g. "Employee", "Equipment") */
    suggestionModel?: string
    /** The field name for the companyTextFieldOptions search (e.g. "department") */
    suggestionFieldName?: string
}

/**
 * AG-Grid cell editor for entering multiple string values as tags.
 * Values are stored as a string[] array.
 * When suggestionModel and suggestionFieldName are provided (and the feature flag is enabled),
 * fetches distinct custom field values from the API and shows them as autocomplete suggestions.
 */
const StringTagCellEditor = forwardRef((props: tStringTagCellEditorParams, ref) => {
    /**
     * Parse the input value into a string array.
     * Handles both string[] and null/undefined inputs.
     */
    const parseValues = (value: unknown): string[] => {
        if (!value) return []
        if (Array.isArray(value)) {
            return value.filter((item): item is string => typeof item === "string" && item.trim() !== "")
        }
        return []
    }

    const [values, setValues] = useState<string[]>(parseValues(props.value))
    const [inputValue, setInputValue] = useState("")
    const [error, setError] = useState("")
    const inputRef = useRef<HTMLInputElement>(null)
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // Fetch suggestions from the API on mount when suggestion params are provided
    const [allSuggestions, setAllSuggestions] = useState<string[]>([])

    useEffect(() => {
        if (!props.suggestionModel || !props.suggestionFieldName) {
            return
        }

        getDataFromSearchv4("", "companyTextFieldOptions", {
            model: props.suggestionModel,
            name: props.suggestionFieldName,
        })
            .then((response: Array<{ name: string }>) => {
                const names = response.map((item: { name: string }) => item.name).filter(Boolean)
                setAllSuggestions(names)
            })
            .catch(() => {
                // Silently fail — suggestions are optional
            })
    }, [props.suggestionModel, props.suggestionFieldName])

    const suggestionState = useSuggestions({
        allSuggestions,
        inputValue,
        excludeValues: values,
        onSelect: (value: string) => {
            setValues(prev => [...prev, value])
            setInputValue("")
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
        showAllOnEmpty: true,
    })

    /**
     * Show a temporary error message that auto-dismisses.
     */
    const showTemporaryError = (message: string) => {
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current)
        }
        setError(message)
        errorTimeoutRef.current = setTimeout(() => {
            setError("")
            errorTimeoutRef.current = null
        }, WARNING_MESSAGE_DURATION)
    }

    useImperativeHandle(ref, () => ({
        /**
         * Returns the current values as a string array.
         * Includes any text currently in the input field.
         */
        getValue: () => {
            if (inputValue.trim()) {
                const finalValues = [...values]
                const trimmedInput = inputValue.trim()
                if (!values.includes(trimmedInput)) {
                    finalValues.push(trimmedInput)
                }
                return finalValues
            }
            return values
        },
        afterGuiAttached: () => {
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
        },
    }))

    useEffect(() => {
        const timer = setTimeout(() => {
            inputRef.current?.focus()
        }, 10)

        return () => {
            clearTimeout(timer)
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current)
            }
        }
    }, [])

    /**
     * Remove a value from the list by index.
     */
    const removeValue = (index: number) => {
        setValues(prev => prev.filter((_, i) => i !== index))
    }

    /**
     * Add a new value to the list.
     * Shows an error if the value is a duplicate.
     */
    const addValue = (value: string) => {
        const trimmed = value.trim()
        if (!trimmed) return

        if (values.includes(trimmed)) {
            showTemporaryError(`"${trimmed}" is already in the list`)
            return
        }

        setValues(prev => [...prev, trimmed])
        setInputValue("")
    }

    /**
     * Handle paste event - split by commas and add multiple values.
     */
    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault()
        const pastedText = e.clipboardData.getData("text")
        const pastedValues = pastedText
            .split(/[,;]/)
            .map(v => v.trim())
            .filter(v => v !== "")

        const newValues: string[] = []
        const duplicates: string[] = []

        pastedValues.forEach(value => {
            if (values.includes(value) || newValues.includes(value)) {
                duplicates.push(value)
            } else {
                newValues.push(value)
            }
        })

        if (newValues.length > 0) {
            setValues(prev => [...prev, ...newValues])
            setInputValue("")
        }

        if (duplicates.length > 0) {
            showTemporaryError(`Skipped duplicate(s): ${duplicates.join(", ")}`)
        }
    }

    /**
     * Handle keyboard events for adding/removing values.
     * Uses comma/semicolon/enter to add (not space, since values can contain spaces).
     * When suggestions are visible, delegates to suggestion navigation first.
     */
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        // Delegate to suggestion navigation first
        if (suggestionState.handleSuggestionKeyDown(e)) return

        if (e.key === "," || e.key === ";") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Enter") {
            e.preventDefault()
            if (inputValue.trim()) {
                addValue(inputValue)
            }
        } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
            removeValue(values.length - 1)
        }
    }

    return (
        <TagEditorContainer>
            <TagsContainer>
                {values.map((value, index) => (
                    <Tag key={index}>
                        <TagText>{value}</TagText>
                        <RemoveButton
                            onClick={() => removeValue(index)}
                            type="button"
                            aria-label={`Remove ${value}`}
                        >
                            <IconX color={colorMiscBlue70} height={14} width={14} />
                        </RemoveButton>
                    </Tag>
                ))}
                <TagInput
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    onChange={e => {
                        setInputValue(e.target.value)
                        suggestionState.setSelectedSuggestionIndex(-1)
                    }}
                    onKeyDown={handleKeyDown}
                    onKeyDownCapture={suggestionState.handleSuggestionKeyDownCapture}
                    onPaste={handlePaste}
                    onBlur={e => {
                        if (suggestionState.isSuggestionBlur(e)) return
                        setTimeout(() => {
                            suggestionState.setShowSuggestions(false)
                            if (inputValue.trim()) {
                                addValue(inputValue)
                            }
                        }, 200)
                    }}
                    onFocus={suggestionState.handleSuggestionFocus}
                    placeholder={values.length === 0 ? "Type and press Enter..." : "Add more..."}
                />
            </TagsContainer>
            <SuggestionsDropdown {...suggestionState} />
            {error && <ErrorMessage>{error}</ErrorMessage>}
        </TagEditorContainer>
    )
})

export default StringTagCellEditor
